import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import accessLocalStorage from '../../../services/accessLocalStorage';
import { readAllTenant } from '../../../services/apiDashboard';
import {
  createCrossCallGrant,
  deleteCrossCallGrant,
  listCrossCallGrants,
} from '../../../services/crossCallGrantsApi';
import ConfirmModal from '../../Common/ConfirmModal';
import Loading from '../../Loading/LoadingModule';
import '../../../libs/bulma.min.css';
import './CrossTenantCalls.css';

const formatDate = (value) => new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
}).format(new Date(value));

function CrossTenantCalls() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [grants, setGrants] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenant, setTenant] = useState('');
  const [peerTenant, setPeerTenant] = useState('');
  const [bidirectional, setBidirectional] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [grantsResponse, tenantsResponse] = await Promise.all([
      listCrossCallGrants(),
      readAllTenant(),
    ]);

    if (grantsResponse.status !== 200) {
      setFeedback({
        ok: false,
        message: grantsResponse.data.message || 'Nao foi possivel carregar as liberacoes.',
      });
      setGrants([]);
    } else {
      setGrants(grantsResponse.data);
    }

    const tenantRows = Array.isArray(tenantsResponse) ? tenantsResponse : [];
    setTenants(
      tenantRows
        .filter((row) => row.active && /^[a-z0-9]{2,8}$/.test(row.short_name))
        .sort((left, right) => left.name.localeCompare(right.name))
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    const session = accessLocalStorage.getUserLocalStorage();
    const user = session && session.user;
    if (!user) {
      history.push('/');
      return;
    }
    if (user.role !== 'root' || user.idtenant !== 'vgs') {
      history.push('/home');
      return;
    }
    loadData();
  }, [history, loadData]);

  const availablePeers = useMemo(
    () => tenants.filter((row) => row.short_name !== tenant),
    [tenant, tenants]
  );

  const submit = async (event) => {
    event.preventDefault();
    if (!tenant || !peerTenant || tenant === peerTenant) return;

    setSaving(true);
    setFeedback(null);
    const response = await createCrossCallGrant({ tenant, peerTenant, bidirectional });
    if (response.status === 200 || response.status === 201) {
      setFeedback({
        ok: true,
        message: response.data.noop
          ? 'A liberacao ja estava configurada.'
          : 'Liberacao aplicada e dialplan recarregado.',
      });
      setTenant('');
      setPeerTenant('');
      await loadData();
    } else {
      setFeedback({
        ok: false,
        message: response.data.message || 'Nao foi possivel aplicar a liberacao.',
      });
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!pendingRemoval) return;
    setSaving(true);
    const response = await deleteCrossCallGrant(
      pendingRemoval.id,
      pendingRemoval.bidirectional
    );
    if (response.status === 200) {
      setFeedback({ ok: true, message: 'Liberacao removida e dialplan recarregado.' });
      setPendingRemoval(null);
      await loadData();
    } else {
      setFeedback({
        ok: false,
        message: response.data.message || 'Nao foi possivel remover a liberacao.',
      });
    }
    setSaving(false);
  };

  if (loading) return <Loading />;

  return (
    <div className="cross-tenant-page">
      <div className="cross-tenant-content">
        <header className="cross-tenant-header">
          <div className="cross-tenant-title-wrapper">
            <svg className="cross-tenant-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h8m0 0-3-3m3 3-3 3m3 7H8m0 0 3 3m-3-3 3-3" />
            </svg>
            <div>
              <h1 className="cross-tenant-title">Liberações Cross-Tenant</h1>
              <p className="cross-tenant-subtitle">
                Autorize chamadas diretas entre clientes específicos sem liberar os demais.
              </p>
            </div>
          </div>
        </header>

        {feedback && (
          <article className={`cross-tenant-alert ${feedback.ok ? 'is-success' : 'is-danger'}`}>
            <span>{feedback.message}</span>
            <button type="button" onClick={() => setFeedback(null)} aria-label="Fechar">×</button>
          </article>
        )}

        <form className="cross-tenant-card cross-tenant-form-card" onSubmit={submit}>
          <div className="cross-tenant-card-heading">
            <h2>Nova liberação</h2>
            <p>Defina quais clientes poderão realizar chamadas diretas entre si.</p>
          </div>
          <div className="cross-tenant-form-grid">
            <div className="cross-tenant-field">
              <label className="cross-tenant-label" htmlFor="cross-tenant-source">Origem</label>
              <select
                id="cross-tenant-source"
                className="cross-tenant-select"
                value={tenant}
                onChange={(event) => {
                  setTenant(event.target.value);
                  if (event.target.value === peerTenant) setPeerTenant('');
                }}
              >
                <option value="">Selecione o tenant que vai discar</option>
                {tenants.map((row) => (
                  <option key={row.short_name} value={row.short_name}>
                    {row.name} ({row.short_name})
                  </option>
                ))}
              </select>
            </div>

            <div className="cross-tenant-field">
              <label className="cross-tenant-label" htmlFor="cross-tenant-destination">Destino</label>
              <select
                id="cross-tenant-destination"
                className="cross-tenant-select"
                value={peerTenant}
                onChange={(event) => setPeerTenant(event.target.value)}
              >
                <option value="">Selecione o tenant de destino</option>
                {availablePeers.map((row) => (
                  <option key={row.short_name} value={row.short_name}>
                    {row.name} ({row.short_name})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="cross-tenant-option-row">
            <label className="cross-tenant-checkbox">
              <input
                type="checkbox"
                checked={bidirectional}
                onChange={(event) => setBidirectional(event.target.checked)}
              />
              <span>
                <strong>Liberação bidirecional</strong>
                <small>Permitir também que o destino chame a origem.</small>
              </span>
            </label>
          </div>

          <div className="cross-tenant-form-actions">
            <button
              className="cross-tenant-submit"
              type="submit"
              disabled={saving || !tenant || !peerTenant}
            >
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              {saving ? 'Aplicando...' : 'Aplicar liberação'}
            </button>
          </div>
        </form>

        <section className="cross-tenant-card">
          <div className="cross-tenant-table-title">
            <div>
              <h2>Liberações ativas</h2>
              <p>Relações de chamadas autorizadas entre clientes.</p>
            </div>
            <span>{grants.length} direção(ões)</span>
          </div>
          {grants.length === 0 ? (
            <div className="cross-tenant-empty">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7h8m0 0-3-3m3 3-3 3m3 7H8m0 0 3 3m-3-3 3-3" />
              </svg>
              <p>Nenhuma liberação cross-tenant configurada.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table is-fullwidth is-hoverable cross-tenant-table">
                <thead>
                  <tr>
                    <th>Origem</th>
                    <th>Destino</th>
                    <th>Modo</th>
                    <th>Criado por</th>
                    <th>Data</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {grants.map((grant) => (
                    <tr key={grant.id}>
                      <td>{grant.tenantName} <small>({grant.tenant})</small></td>
                      <td>{grant.peerTenantName} <small>({grant.peerTenant})</small></td>
                      <td>
                        <span className={`tag ${grant.bidirectional ? 'is-info' : 'is-light'}`}>
                          {grant.bidirectional ? 'Bidirecional' : 'Direcional'}
                        </span>
                      </td>
                      <td>{grant.createdBy}</td>
                      <td>{formatDate(grant.createdAt)}</td>
                      <td>
                        <button
                          type="button"
                          className="cross-tenant-remove"
                          onClick={() => setPendingRemoval(grant)}
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <ConfirmModal
        open={Boolean(pendingRemoval)}
        title="Remover liberação"
        body={pendingRemoval
          ? `Remover ${pendingRemoval.tenant} → ${pendingRemoval.peerTenant}${pendingRemoval.bidirectional ? ' e o sentido inverso' : ''}?`
          : ''}
        confirmLabel="Remover"
        loadingLabel="Removendo..."
        danger
        loading={saving}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={remove}
      />
    </div>
  );
}

export default CrossTenantCalls;
